import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type DateRangeValue = [Dayjs | null, Dayjs | null];
export type FilterDateRangeFormat = "date" | "datetime";

export type DateFilterApiParams = {
  exact?: string;
  exactAsRange?: boolean;
  gt?: string;
  lt?: string;
  gte?: string;
  lte?: string;
  shiftExclusiveDateBounds?: boolean;
};

export interface GenericCrudTableDateColumnDef {
  id?: string;
  filterVariant?: string;
  allowDateRangeFiltering?: boolean;
  customColumnFilterId?: string;
  customFilterParameter?: string;
  dateFilterApiParams?: DateFilterApiParams;
  setNoFilterParameter?: boolean;
  datePicker?: boolean;
  filterDateRangeFormat?: FilterDateRangeFormat;
  Filter?: unknown;
  minSize?: number;
  enableColumnFilterModes?: boolean;
}

export const resolveFilterDateRangeFormat = (
  columnDef: GenericCrudTableDateColumnDef
): FilterDateRangeFormat =>
  columnDef.filterDateRangeFormat ??
  (columnDef.datePicker ? "date" : "datetime");

export type ExactDateFilterMapping = (
  formattedDate: string,
  filter: { customFilterId?: string; columnId: string }
) => Record<string, string> | undefined;

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== "";

const toDayjs = (value: unknown): Dayjs | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const parsed = dayjs(value as dayjs.ConfigType);
  return parsed.isValid() ? parsed : null;
};

export const isDateRangeFilterEnabled = (
  columnDef: GenericCrudTableDateColumnDef,
  allowsDateRangeFilters = true
): boolean => {
  if (!allowsDateRangeFilters) {
    return false;
  }
  if (columnDef.filterVariant !== "date") {
    return false;
  }
  if (columnDef.allowDateRangeFiltering === false) {
    return false;
  }
  if (columnDef.Filter) {
    return false;
  }
  return columnDef.allowDateRangeFiltering ?? true;
};

export const supportsDateRangeApiFiltering = (
  columnDef: GenericCrudTableDateColumnDef,
  allowsDateRangeFilters = true
): boolean => {
  if (!allowsDateRangeFilters) {
    return false;
  }
  if (columnDef.filterVariant !== "date") {
    return false;
  }
  if (columnDef.allowDateRangeFiltering === false) {
    return false;
  }
  return columnDef.allowDateRangeFiltering ?? true;
};

export const resolveDateFilterFieldPath = (
  columnDef: GenericCrudTableDateColumnDef,
  columnId: string
): string => columnDef.customColumnFilterId || columnId;

export const normalizeDateRangeValue = (value: unknown): DateRangeValue => {
  if (!Array.isArray(value)) {
    return [null, null];
  }
  return [toDayjs(value[0]), toDayjs(value[1])];
};

export const isDateRangeValueEmpty = (range: DateRangeValue): boolean =>
  !range[0] && !range[1];

export const isFullDateRangeValue = (range: DateRangeValue): boolean =>
  Boolean(range[0] && range[1]);

export const isDateRangeValueInvalid = (range: DateRangeValue): boolean => {
  const rawStart = range[0];
  const rawEnd = range[1];

  if (rawStart && !dayjs(rawStart).isValid()) {
    return true;
  }
  if (rawEnd && !dayjs(rawEnd).isValid()) {
    return true;
  }
  if (rawStart && rawEnd && dayjs(rawStart).isAfter(dayjs(rawEnd), "day")) {
    return true;
  }
  return false;
};

export const isExactDateFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  return dayjs(value as dayjs.ConfigType).isValid();
};

export const isClosedDateRangeFilterValue = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }
  return isFullDateRangeValue(normalizeDateRangeValue(value));
};

/** @deprecated Use isClosedDateRangeFilterValue or isAnyDateRangeFilterValue */
export const isDateRangeFilterValue = isClosedDateRangeFilterValue;

export const isOpenEndedFromFilterValue = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }
  const range = normalizeDateRangeValue(value);
  return Boolean(range[0] && !range[1]);
};

export const isOpenEndedBeforeFilterValue = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }
  const range = normalizeDateRangeValue(value);
  return Boolean(!range[0] && range[1]);
};

export const isAnyDateRangeFilterValue = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }
  const range = normalizeDateRangeValue(value);
  return Boolean(range[0] || range[1]);
};

export const isCommittedDateRangeFilterValue = (value: unknown): boolean => {
  if (!isAnyDateRangeFilterValue(value)) {
    return false;
  }
  const range = normalizeDateRangeValue(value);
  return !isDateRangeValueInvalid(range);
};

export const hasActiveDateFilter = (
  columnDef: GenericCrudTableDateColumnDef,
  value: unknown,
  allowsDateRangeFilters = true
): boolean => {
  if (columnDef.filterVariant !== "date") {
    return false;
  }
  if (
    isDateRangeFilterEnabled(columnDef, allowsDateRangeFilters) &&
    isAnyDateRangeFilterValue(value)
  ) {
    return !isDateRangeValueEmpty(normalizeDateRangeValue(value));
  }
  return isPresent(value);
};

export const buildClosedDateRangeApiParams = (
  fieldPath: string,
  range: DateRangeValue,
  filterDateRangeFormat: FilterDateRangeFormat = "datetime",
  dateFilterApiParams?: DateFilterApiParams
): Record<string, string> => {
  const params: Record<string, string> = {};
  const [start, end] = range;
  const useDateOnly = filterDateRangeFormat === "date";
  const shiftExclusiveDateBounds =
    useDateOnly && dateFilterApiParams?.shiftExclusiveDateBounds;

  if (start) {
    const usesExclusiveStartParam =
      !dateFilterApiParams?.gte && Boolean(dateFilterApiParams?.gt);
    params[
      dateFilterApiParams?.gte ?? dateFilterApiParams?.gt ?? `${fieldPath}__gte`
    ] = useDateOnly
      ? dayjs(start)
          .add(
            shiftExclusiveDateBounds && usesExclusiveStartParam ? -1 : 0,
            "day"
          )
          .format("YYYY-MM-DD")
      : dayjs(start).startOf("day").toISOString();
  }
  if (end) {
    const usesExclusiveEndParam =
      !dateFilterApiParams?.lte && Boolean(dateFilterApiParams?.lt);
    params[
      dateFilterApiParams?.lte ?? dateFilterApiParams?.lt ?? `${fieldPath}__lte`
    ] = useDateOnly
      ? dayjs(end)
          .add(shiftExclusiveDateBounds && usesExclusiveEndParam ? 1 : 0, "day")
          .format("YYYY-MM-DD")
      : dayjs(end).endOf("day").toISOString();
  }

  return params;
};

export const buildOpenEndedDateApiParams = (
  fieldPath: string,
  range: DateRangeValue,
  filterDateRangeFormat: FilterDateRangeFormat = "datetime",
  dateFilterApiParams?: DateFilterApiParams
): Record<string, string> => {
  const params: Record<string, string> = {};
  const [start, end] = range;
  const useDateOnly = filterDateRangeFormat === "date";
  const shiftExclusiveDateBounds =
    useDateOnly && dateFilterApiParams?.shiftExclusiveDateBounds;

  if (start) {
    params[dateFilterApiParams?.gt ?? `${fieldPath}__gt`] = useDateOnly
      ? dayjs(start)
          .add(shiftExclusiveDateBounds ? -1 : 0, "day")
          .format("YYYY-MM-DD")
      : dayjs(start).startOf("day").toISOString();
  }
  if (end) {
    params[dateFilterApiParams?.lt ?? `${fieldPath}__lt`] = useDateOnly
      ? dayjs(end)
          .add(shiftExclusiveDateBounds ? 1 : 0, "day")
          .format("YYYY-MM-DD")
      : dayjs(end).endOf("day").toISOString();
  }

  return params;
};

export const buildDateRangeApiParams = (
  fieldPath: string,
  range: DateRangeValue,
  filterDateRangeFormat: FilterDateRangeFormat = "datetime",
  dateFilterApiParams?: DateFilterApiParams
): Record<string, string> => {
  const [start, end] = range;

  if (start && end) {
    return buildClosedDateRangeApiParams(
      fieldPath,
      range,
      filterDateRangeFormat,
      dateFilterApiParams
    );
  }

  return buildOpenEndedDateApiParams(
    fieldPath,
    range,
    filterDateRangeFormat,
    dateFilterApiParams
  );
};

export const buildDateFilterApiParams = (
  fieldPath: string,
  value: unknown,
  filterDateRangeFormat: FilterDateRangeFormat = "datetime"
): Record<string, string> => {
  if (isAnyDateRangeFilterValue(value)) {
    const range = normalizeDateRangeValue(value);
    if (isDateRangeValueEmpty(range)) {
      return {};
    }
    return buildDateRangeApiParams(fieldPath, range, filterDateRangeFormat);
  }

  return {};
};

export const buildExactDateApiParams = (
  fieldPath: string,
  value: unknown,
  options: {
    customFilterId?: string;
    columnId: string;
    customParam?: string;
    useGenericParam?: boolean;
    dateFilterApiParams?: DateFilterApiParams;
    mapExactDate?: ExactDateFilterMapping;
  }
): Record<string, string> => {
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) {
    return {};
  }

  if (options.dateFilterApiParams?.exactAsRange) {
    return buildClosedDateRangeApiParams(
      fieldPath,
      [dayjs(date), dayjs(date)],
      "datetime",
      options.dateFilterApiParams
    );
  }

  const formatted = dayjs(date).format("YYYY-MM-DD");

  if (options.dateFilterApiParams?.exact) {
    return { [options.dateFilterApiParams.exact]: formatted };
  }

  const mappedByTable = options.mapExactDate?.(formatted, {
    customFilterId: options.customFilterId,
    columnId: options.columnId,
  });
  if (mappedByTable) {
    return mappedByTable;
  }

  if (options.customFilterId) {
    if (options.customParam) {
      return { [`${options.customFilterId}${options.customParam}`]: formatted };
    }
    if (options.useGenericParam === false) {
      return { [options.customFilterId]: formatted };
    }
    return { [`${options.customFilterId}__date`]: formatted };
  }

  return { [`${fieldPath}__date`]: formatted };
};

export const matchesDateRangeClientSide = (
  cellValue: unknown,
  range: DateRangeValue
): boolean => {
  if (cellValue === null || cellValue === undefined || cellValue === "") {
    return false;
  }

  const cellDate = dayjs(cellValue as dayjs.ConfigType);
  if (!cellDate.isValid()) {
    return false;
  }

  const [start, end] = range;

  if (start && cellDate.isBefore(dayjs(start).startOf("day"))) {
    return false;
  }
  if (end && cellDate.isAfter(dayjs(end).endOf("day"))) {
    return false;
  }

  return true;
};

export const formatDateRangeFilterLabel = (
  range: DateRangeValue,
  t: (key: string) => string
): string => {
  const [start, end] = range;
  const startLabel = start ? dayjs(start).format("DD.MM.YYYY") : null;
  const endLabel = end ? dayjs(end).format("DD.MM.YYYY") : null;

  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }
  if (startLabel) {
    return `${t("from_date")} ${startLabel}`;
  }
  if (endLabel) {
    return `${t("to_date")} ${endLabel}`;
  }
  return "";
};

export const extractDateDigits = (text: string): string =>
  text.replace(/\D/g, "").slice(0, 8);

export const isValidDateDigitAtPosition = (
  currentDigits: string,
  digit: number
): boolean => {
  if (!Number.isInteger(digit) || digit < 0 || digit > 9) {
    return false;
  }

  const position = currentDigits.length;

  switch (position) {
    case 0:
      // Day tens: 0-3 (leading 0 allowed, e.g. 09)
      return digit >= 0 && digit <= 3;
    case 1: {
      // Day units: forbid 00, cap 3x at 31
      const dayTens = Number(currentDigits[0]);
      if (dayTens === 0) {
        return digit >= 1 && digit <= 9;
      }
      if (dayTens === 3) {
        return digit <= 1;
      }
      return true;
    }
    case 2:
      // Month tens: 0 or 1
      return digit === 0 || digit === 1;
    case 3: {
      // Month units: forbid 00, cap 1x at 12
      const monthFirst = Number(currentDigits[2]);
      if (monthFirst === 0) {
        return digit >= 1 && digit <= 9;
      }
      if (monthFirst === 1) {
        return digit >= 0 && digit <= 2;
      }
      return false;
    }
    case 4:
      // Year thousands: must be 1 or 2 (no leading 0)
      return digit === 1 || digit === 2;
    default:
      return true;
  }
};

export const sanitizeDateDigits = (rawDigits: string): string => {
  const digits = extractDateDigits(rawDigits);
  let sanitized = "";

  for (const character of digits) {
    const digit = Number(character);
    if (isValidDateDigitAtPosition(sanitized, digit)) {
      sanitized += character;
    }
  }

  return sanitized;
};

export const formatGermanDateDigits = (digits: string): string => {
  const normalized = sanitizeDateDigits(digits);

  if (normalized.length <= 2) {
    return normalized;
  }
  if (normalized.length <= 4) {
    return `${normalized.slice(0, 2)}.${normalized.slice(2)}`;
  }
  return `${normalized.slice(0, 2)}.${normalized.slice(
    2,
    4
  )}.${normalized.slice(4)}`;
};

export const parseDateDigits = (digits: string): Dayjs | null => {
  const normalized = sanitizeDateDigits(digits);
  if (normalized.length !== 8) {
    return null;
  }

  const formatted = formatGermanDateDigits(normalized);
  const parsed = dayjs(formatted, "DD.MM.YYYY", true);
  return parsed.isValid() ? parsed : null;
};

export const parseManualDateInput = (text: string): Dayjs | null => {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  const digitsOnly = extractDateDigits(trimmed);
  if (digitsOnly.length === 8) {
    const fromDigits = parseDateDigits(digitsOnly);
    if (fromDigits) {
      return fromDigits;
    }
  }

  const formats = ["DD.MM.YYYY", "D.M.YYYY", "YYYY-MM-DD", "DD/MM/YYYY"];
  for (const format of formats) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

export const formatDateFilterDisplay = (
  value: unknown,
  t: (key: string) => string
): string => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  if (Array.isArray(value)) {
    return formatDateRangeFilterLabel(normalizeDateRangeValue(value), t);
  }

  const parsed = toDayjs(value);
  return parsed ? parsed.format("DD.MM.YYYY") : String(value);
};

export const toExactDateFilterValue = (date: Dayjs): string =>
  date.startOf("day").toISOString();

export const toDateRangeFilterValue = (
  start: Dayjs,
  end: Dayjs
): [string, string] => [
  start.startOf("day").toISOString(),
  end.startOf("day").toISOString(),
];

export const toOpenEndedFromFilterValue = (date: Dayjs): [string, null] => [
  date.startOf("day").toISOString(),
  null,
];

export const toOpenEndedBeforeFilterValue = (date: Dayjs): [null, string] => [
  null,
  date.startOf("day").toISOString(),
];

export type DateFilterMode = "pick" | "from" | "before";

export const inferDateFilterMode = (value: unknown): DateFilterMode => {
  if (isOpenEndedFromFilterValue(value)) {
    return "from";
  }
  if (isOpenEndedBeforeFilterValue(value)) {
    return "before";
  }
  return "pick";
};
